<!-- Setting yourself up: your full name, then which of the groups and tutoring tied to your
     account are you. Nothing is put together until you tick it and confirm. -->
<script lang="ts">
  import * as H from "../settings/hints.ts";
  import { api } from "../lib/api.ts";
  import { router } from "../lib/router.svelte.ts";
  import { app } from "../lib/store.svelte.ts";

  interface Rec {
    record: string;
    name: string;
    where: string;
    linked: boolean;
    pending: boolean;
  }

  let step = $state<1 | 2 | 3>(1);
  let fullName = $state("");
  let records = $state<Rec[] | null>(null);
  let picked = $state<Record<string, boolean>>({});
  let error = $state("");
  let saving = $state(false);

  $effect(() => {
    api<{ fullName: string; records: Rec[] }>("/api/me/setup")
      .then((r) => {
        fullName = r.fullName;
        records = r.records;
        // Already yours stays ticked; anything new waits for you to say so.
        picked = Object.fromEntries(r.records.map((x) => [x.record, x.linked]));
      })
      .catch((e) => (error = (e as Error).message));
  });

  const nameOk = $derived(fullName.trim().split(/\s+/).length >= 2);

  async function confirm() {
    saving = true;
    error = "";
    try {
      await api("/api/me/setup", {
        body: { fullName: fullName.trim(), records: Object.keys(picked).filter((k) => picked[k]) },
      });
      step = 3;
      app.toast("You're set up");
    } catch (e) {
      error = (e as Error).message;
    } finally {
      saving = false;
    }
  }
</script>

<div class="page setup">
  <div class="page-head">
    <div>
      <h1>Set yourself up</h1>
      <p class="lede">Step {step} of 3: {step === 1 ? "your full name" : step === 2 ? "which of these are you" : "done"}</p>
    </div>
  </div>
  <div class="steps" aria-hidden="true"><span class:on={step >= 1}></span><span class:on={step >= 2}></span><span class:on={step >= 3}></span></div>

  {#if error}<p class="owes">{error}</p>{/if}

  {#if step === 1}
    <form class="card" onsubmit={(e) => { e.preventDefault(); if (nameOk) step = 2; }}>
      <label class="field">
        <span>Full name</span>
        <input class="input" bind:value={fullName} autocomplete="name" placeholder="First and last name" />
      </label>
      {#if H.SETUP_NAME_NOTE.trim()}<p class="muted small">{H.SETUP_NAME_NOTE}</p>{/if}
      <button class="btn primary" disabled={!nameOk}>Next</button>
    </form>
  {:else if step === 2}
    <div class="card">
      {#if !records}
        <p class="muted">Finding your groups and tutoring.</p>
      {:else if !records.length}
        <p>You aren't in any groups or tutoring yet. When you join one it shows up here.</p>
      {:else}
        <p>These are tied to your account. Tick the ones that are you, {fullName.trim()}.</p>
        <div class="recs" role="group" aria-label="Your groups and tutoring">
          {#each records as r (r.record)}
            <label class="rec">
              <input type="checkbox" bind:checked={picked[r.record]} aria-label="{r.name} in {r.where} is me" />
              <span><strong>{r.name}</strong> <span class="muted">in {r.where}</span>{#if r.pending}<span class="badge">Waiting for your yes</span>{/if}</span>
            </label>
          {/each}
        </div>
      {/if}
      <div class="row-btns">
        <button class="btn" onclick={() => (step = 1)}>Back</button>
        <button class="btn primary" onclick={confirm} disabled={saving || !records}>{saving ? "Saving" : "Confirm"}</button>
      </div>
    </div>
  {:else}
    <div class="card">
      <p>All set, {fullName.trim()}. Your balances across groups and tutoring now show as one.</p>
      <button class="btn primary" onclick={() => router.go("/everyone")}>See your balances</button>
    </div>
  {/if}
</div>

<style>
  .setup .card {
    display: grid;
    gap: 12px;
    max-width: 560px;
    padding: 18px;
    border-radius: var(--r-lg);
    background: var(--surface);
    box-shadow: inset 0 0 0 1px var(--rule);
  }
  .steps {
    display: flex;
    gap: 6px;
    max-width: 560px;
    margin-bottom: 14px;
  }
  .steps span {
    flex: 1;
    height: 4px;
    border-radius: 2px;
    background: var(--wash);
  }
  .steps span.on {
    background: var(--ink-2);
  }
  .recs {
    display: grid;
    gap: 6px;
  }
  .rec {
    display: flex;
    align-items: center;
    gap: 10px;
    padding: 10px 12px;
    border-radius: var(--r-md);
    box-shadow: inset 0 0 0 1px var(--rule);
  }
  .badge {
    margin-left: 6px;
    padding: 1px 7px;
    border-radius: 999px;
    background: var(--wash);
    color: var(--muted);
    font-size: var(--t-xs);
  }
  .row-btns {
    display: flex;
    gap: 8px;
  }
  .small {
    font-size: var(--t-sm);
  }
</style>
